import { OrbitControls, Stars, Text } from '@react-three/drei';
import { Canvas, type ThreeEvent } from '@react-three/fiber';
import { Loader2, RotateCcw } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';

import { Button } from '@/components/ui/button';
import { TruncatedTooltip } from '@/components/ui/truncated-tooltip';
import type { OpportunityMapItem, OpportunityMapResponse } from '@/hooks/api/useOpportunities';
import { cn } from '@/lib/utils';

import {
  MAP_HALF_EXTENT,
  MAP_MAX_Y,
  buildOpportunityMapPoints,
  getOpportunityTooltipRows,
  type OpportunityMapPoint,
} from './opportunity-map-utils';

interface OpportunityMapViewProps {
  data?: OpportunityMapResponse;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
  fullBleed?: boolean;
  theme?: 'light' | 'dark';
}

interface HoverState {
  item: OpportunityMapItem;
  x: number;
  y: number;
}

const CLICK_MOVE_TOLERANCE_PX = 5;
const AUTO_ROTATE_RESUME_DELAY_MS = 1800;
const AXIS_VISUAL_LENGTH = 34;
const HIT_RADIUS_MULTIPLIER = 1.45;
const MIN_HIT_RADIUS = 0.13;
const MAP_THEME = {
  dark: {
    background: 'hsl(210, 45%, 6%)',
    shellClassName: 'bg-background',
    loadingTextClassName: 'text-slate-300',
    loadingCardClassName: 'border-white/10 bg-black/55 text-slate-300',
    messageTitleClassName: 'text-white',
    messageBodyClassName: 'text-slate-400',
    labelOutlineColor: 'hsl(210, 45%, 6%)',
    axes: {
      account: '#94a3b8',
      score: '#94a3b8',
      surrender: '#94a3b8',
      accountLabel: '#cbd5e1',
      scoreLabel: '#cbd5e1',
      surrenderLabel: '#cbd5e1',
      opacity: 0.52,
      blending: THREE.AdditiveBlending,
    },
    scoreColors: {
      target: '#a7b0bd',
      low: '#ff8a1f',
    },
  },
  light: {
    background: 'hsl(210, 20%, 96%)',
    shellClassName: 'bg-background',
    loadingTextClassName: 'text-slate-700',
    loadingCardClassName: 'border-slate-200 bg-white/85 text-slate-700 shadow-elevation-1',
    messageTitleClassName: 'text-slate-950',
    messageBodyClassName: 'text-slate-500',
    labelOutlineColor: 'hsl(210, 20%, 96%)',
    axes: {
      account: '#64748b',
      score: '#64748b',
      surrender: '#64748b',
      accountLabel: '#475569',
      scoreLabel: '#475569',
      surrenderLabel: '#475569',
      opacity: 0.72,
      blending: THREE.NormalBlending,
    },
    scoreColors: {
      target: '#94a3b8',
      low: '#ea7600',
    },
  },
} as const;
const POINT_VERTEX_SHADER = `
  attribute float size;
  attribute vec3 color;
  varying vec3 vColor;

  void main() {
    vColor = color;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mvPosition;
    gl_PointSize = clamp(size * (20.0 / max(1.0, -mvPosition.z)), 3.0, 14.0);
  }
`;
const POINT_FRAGMENT_SHADER = `
  uniform float uOpacity;
  varying vec3 vColor;

  void main() {
    float distanceFromCenter = length(gl_PointCoord - vec2(0.5));
    float core = 1.0 - smoothstep(0.2, 0.24, distanceFromCenter);
    float halo = smoothstep(0.46, 0.24, distanceFromCenter) * 0.22;
    float alpha = (core + halo) * uOpacity;
    if (distanceFromCenter > 0.48 || alpha < 0.02) discard;
    gl_FragColor = vec4(vColor, alpha);
  }
`;

export function OpportunityMapView({
  data,
  isLoading,
  error,
  onRetry,
  fullBleed = false,
  theme = 'dark',
}: OpportunityMapViewProps) {
  const mapTheme = MAP_THEME[theme];
  const [hovered, setHovered] = useState<HoverState | null>(null);
  const [autoRotate, setAutoRotate] = useState(true);
  const [sceneKey, setSceneKey] = useState(0);
  const idleTimerRef = useRef<number>();
  const navigate = useNavigate();
  const points = useMemo(
    () =>
      data
        ? buildOpportunityMapPoints(data.data, data.domains).map((point) => ({
            ...point,
            color: getThemedScoreColor(point.item.propensityScore, theme),
          }))
        : [],
    [data, theme]
  );
  const pauseAutoRotate = useCallback(() => {
    window.clearTimeout(idleTimerRef.current);
    setAutoRotate(false);
  }, []);
  const resumeAutoRotateSoon = useCallback(() => {
    window.clearTimeout(idleTimerRef.current);
    idleTimerRef.current = window.setTimeout(
      () => setAutoRotate(true),
      AUTO_ROTATE_RESUME_DELAY_MS
    );
  }, []);
  const resetView = useCallback(() => {
    window.clearTimeout(idleTimerRef.current);
    setHovered(null);
    setAutoRotate(true);
    setSceneKey((key) => key + 1);
  }, []);

  useEffect(() => {
    return () => window.clearTimeout(idleTimerRef.current);
  }, []);

  if (isLoading && !data) {
    return <OpportunityMapLoading fullBleed={fullBleed} theme={theme} />;
  }

  if (error) {
    return (
      <MapShell fullBleed={fullBleed} theme={theme}>
        <div className="flex h-full min-h-[420px] items-center justify-center p-6 text-center">
          <div className="max-w-sm space-y-3">
            <p className={cn('text-sm font-medium', mapTheme.messageTitleClassName)}>
              Map data could not be loaded.
            </p>
            <p className={cn('text-xs', mapTheme.messageBodyClassName)}>
              {error instanceof Error ? error.message : 'Please try again.'}
            </p>
            <Button variant="secondary" size="sm" onClick={onRetry}>
              Retry
            </Button>
          </div>
        </div>
      </MapShell>
    );
  }

  if (!data || data.data.length === 0) {
    return (
      <MapShell fullBleed={fullBleed} theme={theme}>
        <div className="flex h-full min-h-[420px] items-center justify-center p-6 text-center">
          <div className="max-w-sm space-y-2">
            <p className={cn('text-sm font-medium', mapTheme.messageTitleClassName)}>
              No opportunities match these filters.
            </p>
            <p className={cn('text-xs', mapTheme.messageBodyClassName)}>
              Adjust the search or filters above to repopulate the 3D map.
            </p>
          </div>
        </div>
      </MapShell>
    );
  }

  const activePoints = points.filter((point) => !point.dimmed);
  const dimmedPoints = points.filter((point) => point.dimmed);

  return (
    <MapShell fullBleed={fullBleed} theme={theme}>
      {hovered && <OpportunityMapTooltip hover={hovered} theme={theme} />}

      <Button
        type="button"
        variant="secondary"
        size="sm"
        className={cn(
          'absolute bottom-3 left-3 z-20 h-8 gap-1.5 border px-2.5 text-xs backdrop-blur-xl',
          theme === 'dark'
            ? 'border-white/10 bg-black/55 text-slate-200 hover:bg-white/10 hover:text-white'
            : 'border-slate-200 bg-white/85 text-slate-700 shadow-elevation-1 hover:bg-white hover:text-slate-950'
        )}
        onClick={resetView}
      >
        <RotateCcw className="h-3.5 w-3.5" />
        Reset view
      </Button>

      <Canvas
        key={sceneKey}
        camera={{ position: [9.5, 6.8, 11], fov: 48, near: 0.02, far: 160 }}
        className={cn('h-full w-full', hovered ? 'cursor-pointer' : 'cursor-grab')}
        dpr={[1, 2]}
        onPointerMissed={() => setHovered(null)}
        onPointerLeave={() => setHovered(null)}
      >
        <color attach="background" args={[mapTheme.background]} />
        <ambientLight intensity={0.22} />
        <pointLight position={[6, 8, 5]} intensity={0.8} color="#bfe8ff" />
        {theme === 'dark' && (
          <Stars radius={80} depth={46} count={2600} factor={4} saturation={0} fade speed={0.35} />
        )}

        <MapAxes theme={theme} />
        <OpportunityNodeLayer
          points={dimmedPoints}
          opacity={0.38}
          theme={theme}
          onHover={setHovered}
          onLeave={() => setHovered(null)}
          onSelect={(item) => navigate(`/opportunities/${item.id}`)}
        />
        <OpportunityNodeLayer
          points={activePoints}
          opacity={0.92}
          theme={theme}
          onHover={setHovered}
          onLeave={() => setHovered(null)}
          onSelect={(item) => navigate(`/opportunities/${item.id}`)}
        />

        <OrbitControls
          makeDefault
          autoRotate={autoRotate}
          autoRotateSpeed={0.28}
          zoomToCursor
          enableDamping
          dampingFactor={0.08}
          maxDistance={80}
          minDistance={0.55}
          target={[0, 0, 0]}
          onStart={pauseAutoRotate}
          onEnd={resumeAutoRotateSoon}
        />
      </Canvas>
    </MapShell>
  );
}

function getThemedScoreColor(score: number, theme: 'light' | 'dark'): string {
  const colors = MAP_THEME[theme].scoreColors;
  return score > 75 ? colors.target : colors.low;
}

function MapShell({
  children,
  fullBleed = false,
  theme = 'dark',
}: {
  children: ReactNode;
  fullBleed?: boolean;
  theme?: 'light' | 'dark';
}) {
  return (
    <div
      className={cn(
        'relative overflow-hidden',
        MAP_THEME[theme].shellClassName,
        fullBleed
          ? 'h-full min-h-0 rounded-none border-0'
          : 'h-[calc(100vh-14rem)] min-h-[520px] rounded-lg border border-white/10 shadow-elevation-2'
      )}
    >
      {children}
    </div>
  );
}

function OpportunityMapLoading({
  fullBleed = false,
  theme = 'dark',
}: {
  fullBleed?: boolean;
  theme?: 'light' | 'dark';
}) {
  const mapTheme = MAP_THEME[theme];

  return (
    <MapShell fullBleed={fullBleed} theme={theme}>
      <div
        className={cn(
          'flex h-full min-h-[420px] items-center justify-center',
          mapTheme.shellClassName,
          mapTheme.loadingTextClassName
        )}
      >
        <div
          className={cn(
            'flex items-center gap-2 rounded-lg border px-3 py-2 text-xs backdrop-blur',
            mapTheme.loadingCardClassName
          )}
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading map
        </div>
      </div>
    </MapShell>
  );
}

function OpportunityNodeLayer({
  points,
  opacity,
  theme,
  onHover,
  onLeave,
  onSelect,
}: {
  points: OpportunityMapPoint[];
  opacity: number;
  theme: 'light' | 'dark';
  onHover: (state: HoverState) => void;
  onLeave: () => void;
  onSelect: (item: OpportunityMapItem) => void;
}) {
  if (points.length === 0) return null;

  return (
    <>
      <VisualPointLayer points={points} opacity={opacity} theme={theme} />
      <OpportunityHitTargetLayer
        points={points}
        onHover={onHover}
        onLeave={onLeave}
        onSelect={onSelect}
      />
    </>
  );
}

function VisualPointLayer({
  points,
  opacity,
  theme,
}: {
  points: OpportunityMapPoint[];
  opacity: number;
  theme: 'light' | 'dark';
}) {
  const color = useMemo(() => new THREE.Color(), []);
  const uniforms = useMemo(
    () => ({
      uOpacity: { value: opacity },
    }),
    [opacity]
  );
  const geometryAttributes = useMemo(() => {
    const positions = new Float32Array(points.length * 3);
    const colors = new Float32Array(points.length * 3);
    const sizes = new Float32Array(points.length);

    points.forEach((point, index) => {
      const [x, y, z] = point.position;
      positions[index * 3] = x;
      positions[index * 3 + 1] = y;
      positions[index * 3 + 2] = z;

      color.set(point.color);
      colors[index * 3] = color.r;
      colors[index * 3 + 1] = color.g;
      colors[index * 3 + 2] = color.b;

      sizes[index] = point.radius * 18 + 3.5;
    });

    return { positions, colors, sizes };
  }, [color, points]);

  return (
    <points frustumCulled={false}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[geometryAttributes.positions, 3]} />
        <bufferAttribute attach="attributes-color" args={[geometryAttributes.colors, 3]} />
        <bufferAttribute attach="attributes-size" args={[geometryAttributes.sizes, 1]} />
      </bufferGeometry>
      <shaderMaterial
        uniforms={uniforms}
        vertexShader={POINT_VERTEX_SHADER}
        fragmentShader={POINT_FRAGMENT_SHADER}
        transparent
        blending={theme === 'dark' ? THREE.AdditiveBlending : THREE.NormalBlending}
        depthWrite={false}
        depthTest={false}
        toneMapped={false}
      />
    </points>
  );
}

function OpportunityHitTargetLayer({
  points,
  onHover,
  onLeave,
  onSelect,
}: {
  points: OpportunityMapPoint[];
  onHover: (state: HoverState) => void;
  onLeave: () => void;
  onSelect: (item: OpportunityMapItem) => void;
}) {
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const cleanupPendingSelectionRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => cleanupPendingSelectionRef.current?.();
  }, []);

  useLayoutEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;

    mesh.frustumCulled = false;
    points.forEach((point, index) => {
      dummy.position.set(...point.position);
      dummy.scale.setScalar(Math.max(MIN_HIT_RADIUS, point.radius * HIT_RADIUS_MULTIPLIER));
      dummy.updateMatrix();
      mesh.setMatrixAt(index, dummy.matrix);
    });

    mesh.instanceMatrix.needsUpdate = true;
  }, [dummy, points]);

  const getPointIndex = (event: ThreeEvent<PointerEvent>) => {
    return event.instanceId;
  };

  const handlePointerMove = (event: ThreeEvent<PointerEvent>) => {
    const pointIndex = getPointIndex(event);
    if (pointIndex == null) return;

    const point = points[pointIndex];
    if (!point) return;

    const target = event.nativeEvent.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    onHover({
      item: point.item,
      x: event.nativeEvent.clientX - rect.left,
      y: event.nativeEvent.clientY - rect.top,
    });
  };

  const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
    const pointIndex = getPointIndex(event);
    if (pointIndex == null) return;

    const point = points[pointIndex];
    if (!point) return;

    cleanupPendingSelectionRef.current?.();

    const startX = event.nativeEvent.clientX;
    const startY = event.nativeEvent.clientY;
    const handleWindowPointerUp = (nativeEvent: PointerEvent) => {
      cleanupPendingSelectionRef.current?.();
      const moved = Math.hypot(nativeEvent.clientX - startX, nativeEvent.clientY - startY);
      if (moved <= CLICK_MOVE_TOLERANCE_PX) {
        onSelect(point.item);
      }
    };
    const cleanup = () => {
      window.removeEventListener('pointerup', handleWindowPointerUp);
      cleanupPendingSelectionRef.current = null;
    };

    cleanupPendingSelectionRef.current = cleanup;
    window.addEventListener('pointerup', handleWindowPointerUp, { once: true });
  };

  const handlePointerOut = () => {
    onLeave();
  };

  return (
    <instancedMesh
      ref={meshRef}
      args={[undefined, undefined, points.length]}
      onPointerMove={handlePointerMove}
      onPointerDown={handlePointerDown}
      onPointerOut={handlePointerOut}
      frustumCulled={false}
    >
      <sphereGeometry args={[1, 8, 8]} />
      <meshBasicMaterial transparent opacity={0.001} depthWrite={false} depthTest={false} />
    </instancedMesh>
  );
}

function MapAxes({ theme }: { theme: 'light' | 'dark' }) {
  const palette = MAP_THEME[theme];

  return (
    <group>
      <AxisCylinder
        position={[0, 0, 0]}
        rotation={[0, 0, Math.PI / 2]}
        color={palette.axes.account}
        theme={theme}
      />
      <AxisCylinder
        position={[0, MAP_MAX_Y / 2, 0]}
        rotation={[0, 0, 0]}
        color={palette.axes.score}
        theme={theme}
      />
      <AxisCylinder
        position={[0, 0, 0]}
        rotation={[Math.PI / 2, 0, 0]}
        color={palette.axes.surrender}
        theme={theme}
      />

      <AxisLabel
        position={[MAP_HALF_EXTENT + 1.15, 0.22, 0]}
        color={palette.axes.accountLabel}
        theme={theme}
      >
        Account Value
      </AxisLabel>
      <AxisLabel
        position={[0.45, MAP_MAX_Y + 0.62, 0]}
        color={palette.axes.scoreLabel}
        theme={theme}
      >
        Replacement Score
      </AxisLabel>
      <AxisLabel
        position={[0, 0.22, MAP_HALF_EXTENT + 1.25]}
        color={palette.axes.surrenderLabel}
        theme={theme}
      >
        Years to Surrender
      </AxisLabel>
    </group>
  );
}

function AxisCylinder({
  position,
  rotation,
  color,
  theme,
}: {
  position: [number, number, number];
  rotation: [number, number, number];
  color: string;
  theme: 'light' | 'dark';
}) {
  const axes = MAP_THEME[theme].axes;

  return (
    <mesh position={position} rotation={rotation}>
      <cylinderGeometry args={[0.005, 0.005, AXIS_VISUAL_LENGTH, 8]} />
      <meshBasicMaterial
        color={color}
        transparent
        opacity={axes.opacity}
        blending={axes.blending}
        depthWrite={false}
      />
    </mesh>
  );
}

function AxisLabel({
  position,
  color,
  theme,
  children,
}: {
  position: [number, number, number];
  color: string;
  theme: 'light' | 'dark';
  children: string;
}) {
  return (
    <Text
      position={position}
      fontSize={0.18}
      color={color}
      anchorX="center"
      anchorY="middle"
      outlineWidth={0.004}
      outlineColor={MAP_THEME[theme].labelOutlineColor}
    >
      {children}
    </Text>
  );
}

function OpportunityMapTooltip({ hover, theme }: { hover: HoverState; theme: 'light' | 'dark' }) {
  const rows = getOpportunityTooltipRows(hover.item);
  const product = hover.item.productName || hover.item.productType;
  const isDark = theme === 'dark';

  return (
    <div
      className={cn(
        'pointer-events-none absolute z-20 w-80 max-w-[calc(100%-2rem)] rounded-lg border p-4 shadow-elevation-3 backdrop-blur',
        isDark
          ? 'border-white/15 bg-black/90 text-white'
          : 'border-slate-200 bg-white/95 text-slate-950'
      )}
      style={{
        left: hover.x + 16,
        top: hover.y + 16,
        transform: hover.x > 520 ? 'translateX(-100%)' : undefined,
      }}
    >
      <div className={cn('mb-3 border-b pb-3', isDark ? 'border-white/10' : 'border-slate-200')}>
        <TruncatedTooltip text={hover.item.carrierName} className="text-sm font-semibold" />
        <TruncatedTooltip
          text={product}
          className={cn('text-xs', isDark ? 'text-slate-300' : 'text-slate-600')}
        />
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        {rows.map((row) => (
          <div key={row.label}>
            <p className="text-[10px] uppercase tracking-wide text-slate-500">{row.label}</p>
            <TruncatedTooltip
              text={row.value}
              className={cn('text-xs font-medium', isDark ? 'text-slate-100' : 'text-slate-900')}
            />
          </div>
        ))}
      </div>
      <p className={cn('mt-3 text-[11px]', isDark ? 'text-slate-400' : 'text-slate-500')}>
        Click to open opportunity details
      </p>
    </div>
  );
}
